// src/modules/supplier/SupplierCategoriesList.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaEdit, 
  FaTrash, 
  FaInbox, 
  FaSortAlphaDown, 
  FaSortAlphaUp, 
  FaSort,
  FaTags 
} from 'react-icons/fa';
import { getCategories, deleteCategory } from './supplierService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import useModal from '../../hooks/useModal';
import { StatusBadge, ResponsiveHeader } from '../../components/styled/CommonStyles';
import {
  GridContainer,
  Card,
  CardHeader,
  CardContent,
  ActionIconsContainer,
  ActionButton,
  EmptyState,
  FilterGroup,
  FilterLabel,
  FilterButton,
  FilterSection
} from '../../components/styled/GridComponents';
import styled from 'styled-components';
import Pagination from '../../components/shared/Pagination';

const CategoryName = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: ${props => props.theme.typography.fontSize.medium};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};

  svg {
    color: ${props => props.theme.colors.primary};
    opacity: 0.8;
  }
`;

const CategoryStats = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  margin-top: ${props => props.theme.spacing(2)};
`;

const StatBadge = styled(StatusBadge)`
  background: ${props => props.theme.colors.primary}22;
  color: ${props => props.theme.colors.primary};
`;

const SupplierCategoriesList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const data = await getCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleSort = (field) => {
    setSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDelete = async () => {
    try {
      await deleteCategory(categoryToDelete);
      setCategories(categories.filter(category => category.id !== categoryToDelete));
    } finally {
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
    }
  };

  const getFilteredCategories = () => {
    let filteredData = [...categories];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter(category => 
        category.name.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    return filteredData.sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      return direction * a.name.localeCompare(b.name);
    });
  };

  // Get current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = getFilteredCategories().slice(indexOfFirstItem, indexOfLastItem);

  console.log(currentItems);

  return (
    <DashboardLayout>
      <ResponsiveHeader
        title="Category Management"
        onSearch={setSearchQuery}
        onAdd={() => navigate('/suppliers/categories/new')}
      />

      <FilterSection>
        <FilterGroup>
          <FilterLabel>Sort by:</FilterLabel>
          <FilterButton 
            isActive={true}
            onClick={() => handleSort('name')}
          >
            {sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />}
            Name
          </FilterButton>
        </FilterGroup>
      </FilterSection>

      <GridContainer>
        {loading ? (
          [...Array(6)].map((_, index) => (
            <Card key={index} isLoading>
              <CardContent />
            </Card>
          ))
        ) : currentItems.length === 0 ? (
          <EmptyState>
            <FaInbox size={48} />
            <p>No categories found. Try adjusting your search or add a new category.</p>
          </EmptyState>
        ) : (
          currentItems.map((category) => (
            <Card key={category.id}>
              <CardHeader>
                <CategoryName>
                  <FaTags />
                  {category.name}
                </CategoryName>
              </CardHeader>

              {category.products?.length || category.suppliers?.length &&(
              <CardContent>
                <CategoryStats>
                {category.suppliers?.length &&(
                  <StatBadge>
                    {category.suppliers?.length || 0} Suppliers
                  </StatBadge>
                )}
                {category.products?.length && (
                  <StatBadge>
                    {category.products?.length || 0} Products
                  </StatBadge>
                )}
                </CategoryStats>
              </CardContent>
              )}

              <ActionIconsContainer>
                <ActionButton 
                  onClick={() => navigate(`/suppliers/categories/edit/${category.id}`)}
                  title="Edit category"
                >
                  <FaEdit />
                </ActionButton>
                <ActionButton 
                  onClick={() => {
                    setCategoryToDelete(category.id);
                    setDeleteModalOpen(true);
                  }}
                  title="Delete category"
                >
                  <FaTrash />
                </ActionButton>
              </ActionIconsContainer>
            </Card>
          ))
        )}
      </GridContainer>

      {!loading && getFilteredCategories().length > itemsPerPage && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={getFilteredCategories().length}
          paginate={setCurrentPage}
          currentPage={currentPage}
        />
      )}

      {deleteModalOpen && (
        <Modal
          title="Delete Category"
          message="Are you sure you want to delete this category? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => {
            setDeleteModalOpen(false);
            setCategoryToDelete(null);
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default SupplierCategoriesList;